import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

const DemoVideoSection = () => {
  const theme = useTheme();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Use Cloudflare CDN URL from env, fallback to local file for development
  const videoSrc = process.env.REACT_APP_DEMO_VIDEO_URL || '/demo_video/demo.mp4';
  const posterSrc = "/demo_video/poster.jpg"; // optional; safe if missing

  // Show a center play affordance when paused (esp. if the browser doesn't show one)
  const showCenterPlay = !isPlaying;

  const play = useCallback(async () => {
    const el = videoRef.current;
    if (!el) return;
    setHasInteracted(true);
    try {
      await el.play();
    } catch (e) {
      // gesture restrictions / playback errors
      // eslint-disable-next-line no-console
      console.warn("Demo video play failed:", e);
    }
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);

    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, []);

  return (
    <Box
      id="demo-video"
      sx={{
        py: { xs: 4, md: 4 },
        bgcolor: 'transparent',
      }}
    >
      <Box>
        <Typography
          variant="h2"
          align="center"
          sx={{
            fontSize: { xs: "2rem", md: "2.5rem" },
            fontWeight: 700,
            mb: 2,
            color: theme.palette.text.primary,
          }}
        >
          Watch the Demo
        </Typography>

        <Typography
          variant="h6"
          align="center"
          sx={{
            color: theme.palette.text.secondary,
            mb: 6,
          }}
        >
          See how Stratovai builds product context from real artifacts in under a minute
        </Typography>

        <Paper
          elevation={3}
          sx={{
            position: "relative",
            paddingTop: "56.25%", // 16:9
            // Pure black behind the video so the embed matches "new tab" perception
            bgcolor: "#000",
            borderRadius: 0,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              // Critical: no opacity/filter/backdrop/blend on parents
              bgcolor: "transparent",
            }}
          >
            <video
              ref={videoRef}
              controls
              controlsList="nodownload"
              preload="metadata"
              playsInline
              poster={posterSrc}
              // Let users click the video surface to play as well (common UX)
              onClick={() => {
                if (!isPlaying) play();
              }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                // Keep content intact (no cropping); wrapper handles all visuals.
                objectFit: "contain",
                display: "block",
                // Critical: no filters/opacity applied to the video element.
                filter: "none",
                opacity: 1,
              }}
            >
              <source src={videoSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {/* Center play button (no dimming overlays, so brightness stays accurate) */}
            {showCenterPlay && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // Do not block native controls; only the button should be clickable.
                  pointerEvents: "none",
                }}
              >
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    play();
                  }}
                  aria-label={hasInteracted ? "Resume demo video" : "Play demo video"}
                  sx={{
                    pointerEvents: "auto",
                    width: { xs: 72, sm: 84, md: 96 },
                    height: { xs: 72, sm: 84, md: 96 },
                    bgcolor: "rgba(0,0,0,0.35)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                    "&:hover": {
                      bgcolor: "rgba(0,0,0,0.45)",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 3,
                    },
                  }}
                >
                  <PlayArrowRoundedIcon
                    sx={{
                      fontSize: { xs: 40, sm: 46, md: 52 },
                      color: "#fff",
                      ml: 0.5, // optical centering for triangle
                    }}
                  />
                </IconButton>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default DemoVideoSection;